/*
 * Copyright (c) 2025. Sergey S. Chernov - All Rights Reserved
 *
 *  You may use, distribute and modify this code under the
 * terms of the private license, which you must obtain from the author
 *
 * To obtain the license, contact the author: https://t.me/real_sergeych or email to
 * real dot sergeych at gmail.
 */

package net.sergeych.kiloparsec

import net.sergeych.mp_logger.LogTag
import net.sergeych.mp_logger.Loggable
import net.sergeych.mp_logger.exception
import net.sergeych.mp_logger.info
import net.sergeych.tools.AtomicCounter
import net.sergeych.utools.firstNonNull
import kotlin.reflect.KClass

private typealias RawCommandHandler<C> = suspend (C, UByteArray) -> UByteArray

private val idCounter = AtomicCounter()

open class LocalInterface<S> : Loggable by LogTag("LocalInterface${idCounter.incrementAndGet()}") {

    private val commands = mutableMapOf<String, RawCommandHandler<S>>()

    /**
     * Instruct the transport to immediately break the connection.
     * This exception is not passed to the remote end, instead, transport device breaks
     * connection to remote when receiving it.
     *
     * Remote interface will throw [RemoteInterface.ClosedException] as the break will be detected. As reaction time
     * it depends on the transport in use, we recommend sending some registered exception first if you need
     * to pass important data, or implement special commands on both sides.
     *
     * __Important note:__ _it is not allowed to throw [RemoteInterface.ClosedException] directly!_
     * This exception is processed internally and can't be sent over the network.
     */
    open class BreakConnectionException(
        text: String = "break connection request",
        val flushSendQueue: Boolean = true,
    ) : RuntimeException(text) {
        override val message: String?
            get() = super.message + " (flush=$flushSendQueue)"
    }

    /**
     * New session creator. Rarely needed directlym it can be used for delegation
     * of local interfaces.
     */
//    var sessionMaker: suspend () -> S = {
//        @Suppress("UNCHECKED_CAST")
//        Unit as? S ?: throw IllegalStateException("newSession handler is not set")
//    }
//        private set

//    /**
//     * Builder-style method to create session. Sets the [sessionMaker] actually.
//     */
//    fun createSession(sessionMaker: suspend () -> S) {
//        this.sessionMaker = sessionMaker
//    }

    /**
     * Define a command body to be executed locally.
     */
    fun <A, R> on(command: Command<A, R>, handler: suspend S.(A) -> R) {
        commands[command.name] = { cxt, args ->
            command.exec(args) { handler(cxt, it) }
        }
    }

    suspend fun execute(
        scope: S,
        name: String,
        packedArgs: UByteArray,
    ): UByteArray =
        (commands[name] ?: throw RemoteInterface.UnknownCommand(name))
            .invoke(scope, packedArgs)


    private val errorByClass = mutableMapOf<KClass<*>, String>()
    private val errorBuilder = mutableMapOf<String, (String, UByteArray?) -> Throwable>()

    /**
     * Register exception for automatic transmission over the kiloparsec connection using its [klass]
     * and possibly override code. In most cases it is simpler to use [registerError].
     */
    fun <T : Throwable> registerErrorClass(
        klass: KClass<T>, code: String = klass.simpleName!!,
        exceptionBuilder: (String, UByteArray?) -> T,
    ) {
        errorByClass[klass] = code
        errorBuilder[code] = exceptionBuilder
    }

    /**
     * Register an exception to be transmitted automatically over the kiloparsec connection.
     * Example:
     * ```kotlin
     * localInterface.registerError { IllegalArgumentException(it) }
     * ```
     */
    inline fun <reified T : Throwable> registerError(
        noinline exceptionBuilder: (String) -> T,
    ) {
        registerErrorClass(T::class) { msg, _ -> exceptionBuilder(msg) }
    }

    val errorProviders = mutableListOf<LocalInterface<*>>()

    fun <I : LocalInterface<*>> addErrorProvider(provider: I) {
        errorProviders += provider
    }

    fun getErrorCode(t: Throwable): String? =
        (t as? ExceptionWithCode)?.code
            ?: errorByClass[t::class] ?: errorProviders.firstNonNull { it.getErrorCode(t) }

    fun encodeError(forId: UInt, t: Throwable): Transport.Block.Error =
        if (t is RemoteInterface.ClosedException) {
            exception { "Illegal attempt to send ClosedException" to t }
            encodeError(forId, RemoteInterface.InternalError("TCE"))
        }
        else
            getErrorCode(t)?.let { Transport.Block.Error(forId, it, t.message) }
                ?: Transport.Block.Error(forId, "UnknownError", "${t::class.simpleName}: ${t.message}")

    open fun getErrorBuilder(code: String): ((String, UByteArray?) -> Throwable)? =
        errorBuilder[code] ?: errorProviders.firstNonNull { it.getErrorBuilder(code) }

    fun decodeError(tbe: Transport.Block.Error): Throwable =
        getErrorBuilder(tbe.code)?.invoke(tbe.message, tbe.extra)
            ?: RemoteInterface.RemoteException(tbe).also {
                info { "can't decode error ${tbe.code}: ${tbe.message}" }
            }

    fun decodeAndThrow(tbe: Transport.Block.Error): Nothing {
        throw decodeError(tbe)
    }


}
